/**
 * Google Tag Manager as the single tag loader.
 *
 * Under GTM one `dataLayer` push feeds every destination: GA4 today, and Meta,
 * TikTok, LinkedIn, Google Ads and Clarity as each is enabled in the container.
 * The loader itself is deferred to idle time; events pushed before it arrives
 * sit in `dataLayer` and are replayed when it initialises, so deferral costs no
 * data, only latency to the first hit.
 *
 * Two rules the rest of this file exists to enforce:
 *
 *   1. **Consent defaults go into the queue before the container script.**
 *      Consent Mode is only meaningful if `consent default` is present when the
 *      first tag evaluates; otherwise the first hit goes out under an implicit
 *      grant. GTM reads those gtag-style commands from the shared `dataLayer`,
 *      which is why `gtmBootstrapScript` still emits them and why it has to be
 *      an inline script placed above the loader.
 *
 *   2. **Event parameters are cleared after every push.** GTM merges each
 *      object pushed to `dataLayer` into a persistent data model. Without a
 *      clear, `cta_id` from a click would still be in the model when the next
 *      `page_view` fires, and the GA4 tag would attach a stale CTA to a page
 *      view. `buildDataLayerPushes` returns the event push and the matching
 *      clear push together so no call site can forget the second half.
 */

// Package self-reference: the apps compile this source without
// allowImportingTsExtensions and the package's own ESM test runner needs an
// extension; the self-reference is the one specifier both resolve.
import { PRIVACY_NOTICE_VERSION } from "@tabbio/utils/analytics/consent";
import { ga4ConsentDefault } from "@tabbio/utils/analytics/ga4";
import type { EventPropertyValue } from "./events.ts";

/** `GTM-XXXXXXX` → the container loader URL. */
export function gtmLibraryUrl(containerId: string) {
  return `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(
    containerId,
  )}`;
}

/**
 * The inline bootstrap for a GTM container, as source text for a `<script>`.
 *
 * Emits, in order: the `dataLayer` array; a gtag shim; Consent Mode defaults
 * (all denied); the two consent-mode settings that previously lived in the GA4
 * `config` call (`url_passthrough` keeps UTMs across redirects when cookies are
 * unavailable, `ads_data_redaction` strips ad identifiers while ads consent is
 * denied); and the `gtm.start` marker the container uses to measure its own
 * load time. GA4's `send_page_view: false` moves into the container's Google tag
 * configuration, which is where per-destination settings belong under GTM.
 */
export function gtmBootstrapScript() {
  return [
    "(function(){if(window.__tabbioGtmBootstrapped)return;window.__tabbioGtmBootstrapped=true;",
    "window.dataLayer = window.dataLayer || [];",
    "function gtag(){dataLayer.push(arguments);}",
    `gtag('consent','default',${JSON.stringify(ga4ConsentDefault())});`,
    "gtag('set','url_passthrough',true);",
    "gtag('set','ads_data_redaction',true);",
    `try { var c=JSON.parse(window.localStorage.getItem('tabbio_privacy_consent_v1')||'null'); if(c&&c.version===2&&c.noticeVersion===${JSON.stringify(PRIVACY_NOTICE_VERSION)}&&typeof c.analytics==='boolean'&&typeof c.marketing==='boolean'&&Number.isFinite(Date.parse(c.decidedAt))&&Number.isFinite(Date.parse(c.updatedAt))){window.__tabbioClarityConsent={analytics_Storage:c.analytics?'granted':'denied',ad_Storage:c.marketing?'granted':'denied'};window.__tabbioAnalyticsGranted=c.analytics;window.__tabbioMarketingGranted=c.marketing;gtag('consent','update',{analytics_storage:c.analytics?'granted':'denied',ad_storage:c.marketing?'granted':'denied',ad_user_data:c.marketing?'granted':'denied',ad_personalization:c.marketing?'granted':'denied'});} } catch(e) {}`,
    "dataLayer.push({'gtm.start': new Date().getTime(), event: 'gtm.js'});",
    "})();",
  ].join("\n");
}

/**
 * The same bootstrap as data rather than source, for the client-rendered app
 * shell that has no server-rendered head to inline a script into.
 */
export function gtmBootstrapPushes(): Record<string, unknown>[] {
  return [{ "gtm.start": Date.now(), event: "gtm.js" }];
}

export type DataLayerObject = Record<string, unknown>;

/**
 * The two pushes that make up one event: the event itself, then a clear of
 * every parameter it set.
 *
 * `event` is GTM's reserved trigger key and is never cleared. A parameter named
 * `event` in the input would hijack the trigger, so it is dropped.
 */
export function buildDataLayerPushes(
  name: string,
  params: Record<string, EventPropertyValue | undefined>,
): [DataLayerObject, DataLayerObject] {
  const eventPush: DataLayerObject = { event: name };
  const clearPush: DataLayerObject = {};
  for (const [key, value] of Object.entries(params)) {
    if (key === "event" || value === undefined) continue;
    eventPush[key] = value;
    clearPush[key] = undefined;
  }
  return [eventPush, clearPush];
}

/**
 * Values that persist across events on purpose, set on identify and cleared on
 * sign-out. The GA4 tags in the container read `user_id`, `user_role` and
 * `locale` from the data model on every hit, which is what joins a person's
 * pre-signup marketing session to their in-app session.
 */
export function buildUserPushes(user: {
  userId: string | null;
  role?: string | null;
  locale?: string | null;
}): DataLayerObject {
  return {
    user_id: user.userId ?? undefined,
    user_role: user.userId ? (user.role ?? undefined) : undefined,
    locale: user.userId ? (user.locale === "ar" ? "ar" : "en") : undefined,
  };
}
